import { observer } from "mobx-react-lite";
import type { FC } from "react";
import type { Control } from "react-hook-form";

import { LabelWithOptional } from "@/components/atoms";
import {
  InputControl,
  SelectOptionsWithoutValidation,
  TextareaControl,
} from "@/components/molecules";
import type { Option } from "@/components/molecules/SelectOptions/BaseSelectOptions";
import { formatPrice } from "@/utils/formatPrice";

import {
  DEFAULT_PRICE_DECIMALS,
  type DishFormType,
} from "../../BrandFormMenu/types";

interface Props {
  control: Control<DishFormType>;
  flagsAndStatuses: Option[];
  disabled: boolean;
  /** Symbol of the menu's currency, shown on the price label. */
  currencySymbol?: string;
  /** Decimals of the menu's currency, used to shape the price placeholder. */
  priceDecimals?: number;
}

const StepDetails: FC<Props> = observer(
  ({
    control,
    flagsAndStatuses,
    disabled,
    currencySymbol,
    priceDecimals = DEFAULT_PRICE_DECIMALS,
  }) => {
    return (
      <div className="flex flex-col gap-6">
        <div>
          <h2 className="text-xl font-bold mb-5">Details</h2>

          <div className="flex flex-col">
            <InputControl
              formControl={control}
              label="Dish name (English)"
              nameField="dishNameEnglish"
              data-testid="dish-drawer-name-en"
              disabled={disabled}
            />
            <InputControl<DishFormType>
              formControl={control}
              label="Dish name (Arabic)"
              nameField="dishNameArabic"
              data-testid="dish-drawer-name-ar"
              disabled={disabled}
            />
            <div className="flex flex-col gap-1 text-sm">
              <LabelWithOptional label="Description (English)" />
              <TextareaControl<DishFormType>
                formControl={control}
                nameField="descriptionEnglish"
                data-testid="dish-drawer-desc-en"
                disabled={disabled}
              />
            </div>
            <div className="flex flex-col gap-1 text-sm">
              <LabelWithOptional label="Description (Arabic)" />
              <TextareaControl<DishFormType>
                formControl={control}
                nameField="descriptionArabic"
                data-testid="dish-drawer-desc-ar"
                disabled={disabled}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col text-sm">
                <LabelWithOptional label="Preparation time (min)" />
                <InputControl<DishFormType>
                  formControl={control}
                  nameField="preparationTime"
                  placeholder="0"
                  classedName="mb-0"
                  data-testid="dish-drawer-prep-time"
                  disabled={disabled}
                />
              </div>

              <InputControl<DishFormType>
                formControl={control}
                label={`Price${currencySymbol ? ` (${currencySymbol})` : ""}`}
                nameField="price"
                // Shaped like the currency, so a Jordanian field hints 0.000
                // and a Saudi one 0.00.
                placeholder={formatPrice(0, priceDecimals)}
                classedName="mb-0"
                data-testid="dish-drawer-price"
                disabled={disabled}
              />
            </div>
          </div>
        </div>

        <div>
          <p className="text-sm font-semibold mb-2">Flags & status</p>
          <SelectOptionsWithoutValidation
            name="flagsAndStatuses"
            form={control}
            selectList={flagsAndStatuses}
            disabled={disabled}
          />
        </div>
      </div>
    );
  }
);

export default StepDetails;
